import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { Link } from "react-router-dom";
import React, { useEffect } from "react";
import Dropdown, {
  dropdownTypes,
} from "@visiblethread/react-components/dist/components/Dropdown";
import ButtonsDropdownOption from "@visiblethread/react-components/dist/components/Dropdown/ButtonsDropdownOption";
import { ReactComponent as PenIcon } from "icons/pen-icon.svg";
import { ReactComponent as EyeIcon } from "icons/eye.svg";
import { ReactComponent as MenuIcon } from "icons/threeDots.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  closeEditMode,
  exportDiscoveryIgnoreList,
  importDiscoveryIgnoreList,
  openEditMode,
  setDiscoveryIgnoreList,
} from "slices/settings/actions/discoveryIgnoreList";
import PropTypes from "prop-types";
import { hideInlineAlert, showInlineAlert } from "slices/app/appSlice";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import { selectDiscoveryIgnore } from "slices/settings/selectors";
import { showModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import { hasRole } from "utils";
import { USER_ROLES } from "constants/app";
import { saveButtonError } from "constants/discovery";
import { TooltipTrigger } from "@visiblethread/react-components/dist/components/Tooltip";

const tooltipText =
  "This is a list of words and phrases that we will ignore when running discovery.";

const Header = ({ hasErrors }) => {
  const dispatch = useDispatch();
  const { editMode, ignoreList, hasBeenEdited, locked, updatedBy } =
    useSelector(selectDiscoveryIgnore);
  // eslint-disable-next-line no-undef
  const { userName } = UserInfo;
  const lockedByOtherUser = locked && updatedBy !== userName;
  const hasPermissions = hasRole(USER_ROLES.discoverySettings);

  useEffect(() => {
    if (!hasPermissions) {
      dispatch(
        showInlineAlert({
          variant: alertVariants.warning,
          text: "You do not have permission to edit Discovery Ignore List",
        })
      );
    } else if (locked && lockedByOtherUser) {
      dispatch(
        showInlineAlert({
          variant: alertVariants.warning,
          text: `You can not edit this list as ${updatedBy} is currently editing it`,
        })
      );
    }

    return () => {
      dispatch(hideInlineAlert());
    };
  }, [hasPermissions, locked, updatedBy]);

  const handleSaveClick = () => {
    dispatch(setDiscoveryIgnoreList(ignoreList));
  };
  const handleCancelClick = () => {
    dispatch(closeEditMode());
  };
  const handleEditClick = () => {
    dispatch(openEditMode());
  };
  const handleExportToCSV = () => {
    dispatch(exportDiscoveryIgnoreList());
  };
  const handleImportFromCSV = () => {
    const onOk = (formData) => dispatch(importDiscoveryIgnoreList(formData));
    dispatch(
      showModal(modalTypes.IMPORT_FILE, {
        title: "Import Discovery Ignore List",
        accepts: ".csv",
        onOk,
        okText: "Import",
      })
    );
  };

  const menuOptions = [
    {
      content: "Export to CSV",
      onClick: handleExportToCSV,
    },
    {
      content: "Import from CSV",
      onClick: handleImportFromCSV,
    },
  ];

  const threeDots = () => (
    <Dropdown
      type={dropdownTypes.buttons}
      propsObject={{
        hideArrowIcon: true,
        button: (
          <span>
            <MenuIcon />
          </span>
        ),
        options: menuOptions.map(({ content, onClick, dataTest }) => (
          <ButtonsDropdownOption
            key={content}
            content={content}
            onClick={onClick}
            disabled={!hasPermissions || locked}
            data-test={dataTest}
          />
        )),
        buttonVariant: buttonVariants.secondary,
      }}
    />
  );

  return (
    <div
      className="discovery_ignore_list__header"
      data-testid="discovery_ignore_list__header"
    >
      <div className="discovery_ignore_list__header-left">
        <h1>Discovery Ignore List</h1>

        <TooltipTrigger content={tooltipText}>
          <div
            className="discovery_ignore_list__header-left__info_icon"
            aria-label="information tooltip icon"
          >
            i
          </div>
        </TooltipTrigger>
        {editMode ? (
          <div className="discovery_ignore_list__header-left__info">
            <PenIcon width={10} height={10} />
            <span>You are editing this Ignore list</span>
          </div>
        ) : (
          <div className="discovery_ignore_list__header-left__info">
            <EyeIcon width={14} height={14} />
            <span>You are viewing this</span>
          </div>
        )}
      </div>
      <div className="discovery_ignore_list__header-right">
        {editMode ? (
          <>
            <Button
              content="Save edits"
              variant={buttonVariants.primary}
              disabled={!hasBeenEdited || hasErrors}
              onClick={handleSaveClick}
              error={hasErrors ? saveButtonError : ""}
            />
            <Link
              to="/discovery"
              className="secondary_link"
              onClick={handleCancelClick}
            >
              Cancel Edit
            </Link>
          </>
        ) : (
          <Button
            content="Edit Ignore List"
            variant={buttonVariants.primary}
            disabled={!hasPermissions || lockedByOtherUser}
            onClick={handleEditClick}
          />
        )}
        {!editMode && threeDots()}
      </div>
    </div>
  );
};

export default Header;

Header.propTypes = {
  hasErrors: PropTypes.bool.isRequired,
};
